import * as TabsPrimitive from "@radix-ui/react-tabs";
import clsx from "clsx";
import React, { createContext, useContext } from "react";

interface TabsGroupProps {
	children?: React.ReactNode;
	label?: string;
}

const TabsContext = createContext<{
	variant: "default" | "sub" | "compact";
}>({
	variant: "default",
});

const Tabs = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> & {
		variant?: "default" | "sub" | "compact";
	}
>(({ className, variant = "default", ...props }, ref) => (
	<TabsContext.Provider value={{ variant }}>
		<TabsPrimitive.Root
			ref={ref}
			className={clsx("flex w-full data-[orientation=vertical]:flex-row data-[orientation=horizontal]:flex-col", className)}
			{...props}
		/>
	</TabsContext.Provider>
));
Tabs.displayName = TabsPrimitive.Root.displayName;

const TabsList = React.forwardRef<React.ElementRef<typeof TabsPrimitive.List>, React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>>(
	({ className, ...props }, ref) => {
		const { variant } = useContext(TabsContext);
		const defaultClasses = "gap-1 data-[orientation=horizontal]:pb-2 data-[orientation=horizontal]:border-b";
		const subTabClasses = "gap-1 data-[orientation=horizontal]:py-3 data-[orientation=horizontal]:border-b-0";
		const compactTabClasses = "px-4 data-[orientation=horizontal]:border-b";

		return (
			<TabsPrimitive.List
				ref={ref}
				className={clsx(
					"flex w-full border-slate-200",
					"data-[orientation=horizontal]:flex data-[orientation=horizontal]:border-slate-200",
					"data-[orientation=vertical]:flex-col data-[orientation=vertical]:pr-8 data-[orientation=vertical]:border-r data-[orientation=vertical]:border-slate-200 data-[orientation=vertical]:max-w-[240px]",
					variant === "default" && defaultClasses,
					variant === "sub" && subTabClasses,
					variant === "compact" && compactTabClasses,
					className,
				)}
				{...props}
			/>
		);
	},
);
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsGroup = ({ children, label }: TabsGroupProps) => (
	<div className="flex flex-col w-full mb-6">
		{label && <span className="pl-3 mb-1.5 font-semibold text-xs uppercase tracking-wide text-primary-600">{label}</span>}
		{children}
	</div>
);

const TabsTrigger = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
		count?: React.ReactNode;
	}
>(({ className, children, count, ...props }, ref) => {
	const { variant } = useContext(TabsContext);
	const defaultClasses = clsx(
		"before:content-[''] before:absolute before:opacity-0 before:pointer-events-none",
		"data-[orientation=horizontal]:before:left-0 data-[orientation=horizontal]:before:right-0 data-[orientation=horizontal]:before:-bottom-2 data-[orientation=horizontal]:before:-mb-[1px] data-[orientation=horizontal]:before:w-full data-[orientation=horizontal]:before:h-[2px]",
		"data-[orientation=vertical]:before:-right-8 data-[orientation=vertical]:before:-mr-[1px] data-[orientation=vertical]:before:w-[2px] data-[orientation=vertical]:before:h-full",
		"data-[state=active]:grayscale-0 data-[state=active]:bg-primary-50 data-[state=active]:text-primary-600 data-[state=active]:before:opacity-100 data-[state=active]:before:bg-primary-600",
	);
	const subTabClasses = "data-[state=active]:bg-slate-100 data-[state=active]:text-foreground-dark";
	const compactTabClasses =
		"-mb-[1px] !text-sm data-[state=active]:bg-transparent data-[state=active]:border data-[state=active]:border-border/50 data-[state=active]:border-b-background data-[state=active]:rounded-none data-[state=active]:rounded-t-md data-[state=active]:text-foreground-dark";

	return (
		<TabsPrimitive.Trigger
			ref={ref}
			disabled={props.disabled}
			className={clsx(
				"relative grayscale text-left inline-flex items-center px-3 py-2.5 gap-1 rounded-md font-semibold text-base text-foreground/70 hover:text-foreground-dark data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
				variant === "default" && defaultClasses,
				variant === "sub" && subTabClasses,
				variant === "compact" && compactTabClasses,
				className,
			)}
			{...props}
		>
			{children}
			{typeof count === "number" && count > 0 && <TabsBadge>{count}</TabsBadge>}
		</TabsPrimitive.Trigger>
	);
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<React.ElementRef<typeof TabsPrimitive.Content>, React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>>(
	({ className, ...props }, ref) => {
		return (
			<TabsPrimitive.Content
				ref={ref}
				className={clsx(
					"w-full ring-offset-background focus-visible:outline-none focus-visible:ring-0 data-[orientation=horizontal]:mt-0 data-[orientation=vertical]:ml-8",
					className,
				)}
				{...props}
			/>
		);
	},
);
TabsContent.displayName = TabsPrimitive.Content.displayName;

const TabsBadge = ({ className, ...props }: { className?: string } & React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={clsx(
			"inline-flex justify-center items-center min-w-[16px] min-h-[16px] px-1 bg-primary-600 text-primary-foreground rounded-full text-xs",
			className,
		)}
		{...props}
	/>
);

export { Tabs, TabsList, TabsTrigger, TabsContent, TabsGroup, TabsBadge };
